// AlertModal.jsx — replaces browser alert() with a clean modal
import React, { useState, useCallback } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

// ── Global alert state ────────────────────────────────────────
let _setAlert = null;

export function showAlert(message, type = 'info', title = '') {
  if (_setAlert) _setAlert({ message, type, title, open: true });
}

export function showError(message, title = 'Error') {
  showAlert(message, 'error', title);
}

export function showSuccess(message, title = 'Success') {
  showAlert(message, 'success', title);
}

export function showWarning(message, title = 'Warning') {
  showAlert(message, 'warning', title);
}

// ── AlertModal component — mount once in App.jsx ─────────────
export default function AlertModal() {
  const [alert, setAlert] = useState({ open: false, message: '', type: 'info', title: '' });
  _setAlert = setAlert;

  if (!alert.open) return null;

  const config = {
    success: { icon: CheckCircleIcon,         bg: '#f0fdf4', border: '#bbf7d0', iconColor: '#16a34a', btnBg: '#16a34a', title: alert.title || 'Success' },
    error:   { icon: XCircleIcon,             bg: '#fef2f2', border: '#fecaca', iconColor: '#dc2626', btnBg: '#dc2626', title: alert.title || 'Error' },
    warning: { icon: ExclamationTriangleIcon, bg: '#fffbeb', border: '#fde68a', iconColor: '#d97706', btnBg: '#d97706', title: alert.title || 'Warning' },
    info:    { icon: InformationCircleIcon,   bg: '#eff6ff', border: '#bfdbfe', iconColor: '#2563eb', btnBg: '#2563eb', title: alert.title || 'Info' },
  };

  const c = config[alert.type] || config.info;
  const Icon = c.icon;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px' }}
      onClick={() => setAlert(a => ({ ...a, open: false }))}>
      <div style={{ background: 'white', borderRadius: '14px', maxWidth: '380px', width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', overflow: 'hidden', animation: 'alertIn 0.2s ease' }}
        onClick={e => e.stopPropagation()}>
        <style>{`@keyframes alertIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}`}</style>

        {/* Header */}
        <div style={{ background: c.bg, borderBottom: `1px solid ${c.border}`, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 0 0 2px ${c.border}` }}>
            <Icon style={{ width: 20, height: 20, color: c.iconColor }} />
          </div>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', flex: 1 }}>{c.title}</span>
          <button onClick={() => setAlert(a => ({ ...a, open: false }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}>
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px 20px' }}>
          <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>{alert.message}</p>
          <button onClick={() => setAlert(a => ({ ...a, open: false }))}
            style={{ width: '100%', padding: '0.65rem', background: c.btnBg, color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
